import React, { useEffect, useMemo, useState } from "react";
import { Plus, X, Loader2, Tag, HelpCircle, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api from "@/lib/api";

/**
 * iter-168 — Compact composer for the two Browse/Match-specific fields on
 * the PostJob form: required_skills (taxonomy-backed) and
 * knockout_questions (employer-defined filters, never auto-rejectors).
 */
export const MatchFieldsSection = ({
    requiredSkills, setRequiredSkills,
    knockoutQuestions, setKnockoutQuestions,
}) => {
    const [taxonomy, setTaxonomy] = useState([]);
    const [taxQuery, setTaxQuery] = useState("");
    const [loadingTax, setLoadingTax] = useState(true);
    const [newQ, setNewQ] = useState("");
    const [newQType, setNewQType] = useState("yes_no");

    useEffect(() => {
        api.get("/skills").then(({ data }) => {
            setTaxonomy((data.skills || []).map((s) => s.label));
        }).catch(() => {}).finally(() => setLoadingTax(false));
    }, []);

    const unselected = useMemo(() => {
        const q = taxQuery.trim().toLowerCase();
        return taxonomy
            .filter((s) => !requiredSkills.includes(s))
            .filter((s) => !q || s.toLowerCase().includes(q))
            .slice(0, 16);
    }, [taxonomy, requiredSkills, taxQuery]);

    const addSkill = (s) => { if (!requiredSkills.includes(s)) setRequiredSkills([...requiredSkills, s]); };
    const removeSkill = (s) => setRequiredSkills(requiredSkills.filter((x) => x !== s));

    const addCustomSkill = () => {
        const s = taxQuery.trim();
        if (!s) return;
        addSkill(s);
        setTaxQuery("");
    };

    const addKnockout = () => {
        const q = newQ.trim();
        if (!q) return;
        setKnockoutQuestions([
            ...knockoutQuestions,
            { id: `k_${Date.now().toString(36)}`, question: q, answer_type: newQType },
        ]);
        setNewQ("");
    };
    const removeKnockout = (id) => setKnockoutQuestions(knockoutQuestions.filter((k) => k.id !== id));

    return (
        <div className="space-y-5" data-testid="post-job-match-section">
            {/* Required skills */}
            <div>
                <div className="flex items-center gap-2">
                    <Tag size={14} className="text-emerald-500" />
                    <label className="text-sm font-semibold text-foreground">Required skills</label>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                    Taxonomy-backed. Browse Talent matches on these — the match line reads &quot;Matches X of Y requirements&quot; per role.
                </p>
                {requiredSkills.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="post-job-skills-selected">
                        {requiredSkills.map((s) => (
                            <Badge key={s} data-testid={`post-job-skill-chip-${s.replace(/\s+/g, "-")}`}
                                   className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 pr-1">
                                {s}
                                <button type="button" onClick={() => removeSkill(s)} className="ml-1 rounded hover:bg-emerald-500/20 p-0.5"
                                        data-testid={`post-job-skill-remove-${s.replace(/\s+/g, "-")}`}>
                                    <X size={10} />
                                </button>
                            </Badge>
                        ))}
                    </div>
                )}
                <div className="mt-2 flex items-center gap-2">
                    <Input value={taxQuery} onChange={(e) => setTaxQuery(e.target.value)}
                           placeholder="Search skills…"
                           data-testid="post-job-skills-search" className="h-8 text-sm flex-1" />
                    <button type="button" onClick={addCustomSkill} disabled={!taxQuery.trim()}
                            data-testid="post-job-skills-add-custom"
                            className="btn-outline text-xs px-2 py-1 inline-flex items-center gap-1 disabled:opacity-60">
                        <Plus size={11} /> Add
                    </button>
                </div>
                {loadingTax ? (
                    <div className="mt-2 py-2 flex items-center gap-2 text-[11px] text-muted-foreground">
                        <Loader2 size={11} className="animate-spin" /> Loading taxonomy…
                    </div>
                ) : (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="post-job-skills-suggestions">
                        {unselected.map((s) => (
                            <button key={s} type="button" onClick={() => addSkill(s)}
                                    data-testid={`post-job-skill-suggest-${s.replace(/\s+/g, "-")}`}
                                    className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-[11px] text-foreground hover:bg-muted/40">
                                <Plus size={9} /> {s}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Knockout questions */}
            <div>
                <div className="flex items-center gap-2">
                    <HelpCircle size={14} className="text-amber-500" />
                    <label className="text-sm font-semibold text-foreground">Knockout questions</label>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                    Must-haves (e.g. &quot;Available weekends?&quot;). Candidates failing a knockout move to a collapsed &quot;Didn&apos;t meet filters&quot; section — never auto-rejected. You decide.
                </p>
                {knockoutQuestions.length > 0 && (
                    <ul className="mt-2 space-y-1.5" data-testid="post-job-knockouts-list">
                        {knockoutQuestions.map((k) => (
                            <li key={k.id} data-testid={`post-job-knockout-${k.id}`}
                                className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-xs">
                                <span className="flex-1 text-foreground">{k.question}</span>
                                <Badge className="bg-muted text-foreground border border-border text-[10px]">
                                    {k.answer_type === "yes_no" ? "Yes/No" : "Short text"}
                                </Badge>
                                <button type="button" onClick={() => removeKnockout(k.id)}
                                        data-testid={`post-job-knockout-remove-${k.id}`}
                                        className="p-1 rounded hover:bg-rose-500/15 text-rose-600 dark:text-rose-400">
                                    <Trash2 size={11} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <div className="mt-2 flex items-center gap-2">
                    <Input value={newQ} onChange={(e) => setNewQ(e.target.value)}
                           placeholder="Available to work weekends?"
                           data-testid="post-job-knockout-input" className="h-8 text-sm flex-1" />
                    <Select value={newQType} onValueChange={setNewQType}>
                        <SelectTrigger data-testid="post-job-knockout-type" className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="yes_no">Yes/No</SelectItem>
                            <SelectItem value="short_text">Short text</SelectItem>
                        </SelectContent>
                    </Select>
                    <button type="button" onClick={addKnockout} disabled={!newQ.trim()}
                            data-testid="post-job-knockout-add"
                            className="btn-outline text-xs px-2 py-1 inline-flex items-center gap-1 disabled:opacity-60">
                        <Plus size={11} /> Add
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MatchFieldsSection;
